import React, { useContext, useEffect } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { EditRestaurantContext, IEditRestaurantContext } from '../../editRestaurantState/editRestaurantContext';
import BankSettingsState from './components/BankSettingsForm/bankSettingsState/BankSettingsState';
import BankSettingsForm from './components/BankSettingsForm/BankSettingsForm';
import EditForm from './components/EditForm/EditForm';
import EditFormForClientManager from './components/EditFormForClientManager/EditFormForClientManager';
import LocalizationForm from './components/LocalizationForm/LocalizationForm';
import StyleForm from './components/StyleForm/StyleForm';
import DevicesForm from './components/DevicesForm/DevicesForm';
import WatchesForm from './components/WatchesForm/WatchesForm';
import { ReportsContainerState } from './components/ReportsContainer/reportsContainerState/ReportsContainerState';
import { ReportsContainer } from './components/ReportsContainer/ReportsContainer';
import EditFormForClientManagerState from './components/EditFormForClientManager/EditFormForClientManagerState/EditFormForClientManagerState';
import LocalizationState from './components/LocalizationForm/localizationState/LocalizationState';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { UserRoles } from 'types';
import { allRoles } from 'appConstants';
import StyleState from './components/StyleForm/styleState/StyleState';
import WatchesState from './components/WatchesForm/watchesState/WatchesState';
import { AndroidWatchesFormState } from './components/AndroidWatchesForm/androidWatchesFormState/AndroidWatchesFormState';
import { AndroidWatchesForm } from './components/AndroidWatchesForm/AndroidWatchesForm';
import { NfcCardsState } from './components/NfcCardsForm/nfcCardsState/NfcCardsState';
import { NfcCardsForm } from './components/NfcCardsForm/NfcCardsForm';
import style from './Forms.module.scss';

export const Forms: React.FC = () => {
	const { featureFlags } = useContext<IAppContext>(AppContext);
	const { changeVisibleForm, visibleForm } = useContext<IEditRestaurantContext>(EditRestaurantContext);
	const hasMainFormAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);
	const hasMainFormForClintManagerAccess = useRoleAccess([UserRoles.CLIENT_MANAGER, UserRoles.GROUP_MANAGER]);
	const hasLocalizationFormAccess = useRoleAccess(allRoles);
	const hasStyleFormAccess = useRoleAccess(allRoles);
	const hasDeviceFormAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);
	const hasWatchesFormAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);
	const hasReportsFormAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);
	const hasBankSettingsFormAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);
	const hasAndroidWatchesFormAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);
	const hasNfcCardsFormAccess = useRoleAccess([UserRoles.SUPER_USER]) && featureFlags.HAS_NFC_CARDS;

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [visibleForm]);

	useEffect(() => {
		if (hasMainFormAccess) {
			changeVisibleForm('mainForm');
		} else if (hasMainFormForClintManagerAccess) {
			changeVisibleForm('mainFormForClientManager');
		} else if (hasLocalizationFormAccess) {
			changeVisibleForm('localizationForm');
		} else if (hasStyleFormAccess) {
			changeVisibleForm('styleForm');
		} else if (hasDeviceFormAccess) {
			changeVisibleForm('devicesForm');
		} else if (hasWatchesFormAccess) {
			changeVisibleForm('watchesForm');
		} else if (hasReportsFormAccess) {
			changeVisibleForm('reportsForm');
		} else if (hasBankSettingsFormAccess) {
			changeVisibleForm('bankSettingsForm');
		} else if (hasAndroidWatchesFormAccess) {
			changeVisibleForm('androidWatchesForm');
		} else if (hasNfcCardsFormAccess) {
			changeVisibleForm('nfcCardsForm');
		}
	}, [
		hasMainFormAccess,
		hasMainFormForClintManagerAccess,
		hasLocalizationFormAccess,
		hasStyleFormAccess,
		hasDeviceFormAccess,
		hasWatchesFormAccess,
		hasReportsFormAccess,
		hasBankSettingsFormAccess,
		hasAndroidWatchesFormAccess,
		hasNfcCardsFormAccess,
	]);

	return (
		<div className={style.container} data-test="Forms">
			{hasMainFormAccess && <EditForm isVisible={visibleForm === 'mainForm'} />}
			{hasMainFormForClintManagerAccess && (
				<EditFormForClientManagerState>
					<EditFormForClientManager isVisible={visibleForm === 'mainFormForClientManager'} />
				</EditFormForClientManagerState>
			)}
			{hasLocalizationFormAccess && (
				<LocalizationState>
					<LocalizationForm isVisible={visibleForm === 'localizationForm'} />
				</LocalizationState>
			)}
			{hasStyleFormAccess && (
				<StyleState>
					<StyleForm isVisible={visibleForm === 'styleForm'} />
				</StyleState>
			)}
			{hasDeviceFormAccess && <DevicesForm isVisible={visibleForm === 'devicesForm'} />}
			{hasWatchesFormAccess && (
				<WatchesState>
					<WatchesForm isVisible={visibleForm === 'watchesForm'} />
				</WatchesState>
			)}
			{hasReportsFormAccess && (
				<ReportsContainerState>
					<ReportsContainer isVisible={visibleForm === 'reportsForm'} />
				</ReportsContainerState>
			)}
			{hasBankSettingsFormAccess && (
				<BankSettingsState>
					<BankSettingsForm isVisible={visibleForm === 'bankSettingsForm'} />
				</BankSettingsState>
			)}
			{hasAndroidWatchesFormAccess && (
				<AndroidWatchesFormState>
					<AndroidWatchesForm isVisible={visibleForm === 'androidWatchesForm'} />
				</AndroidWatchesFormState>
			)}
			{hasNfcCardsFormAccess && (
				<NfcCardsState>
					<NfcCardsForm isVisible={visibleForm === 'nfcCardsForm'} />
				</NfcCardsState>
			)}
		</div>
	);
};
